"use client";

import React, { useEffect, useState, useRef } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronDown, Package, CircleAlert, Loader, ChevronRight, Menu, X } from "lucide-react";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuContent, NavigationMenuTrigger, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
function ProductsListConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex items-center justify-center p-12 w-full">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  if (error) {
    return <div className="p-8 text-center w-full">
        <p className="text-sm text-destructive">Failed to load products</p>
      </div>;
  }
  if (products.length === 0) {
    return <div className="p-12 text-center w-full">
        <p className="text-sm text-muted-foreground">No products found in this category</p>
      </div>;
  }
  return <div className="grid grid-cols-4 gap-2 p-4">
      {products.map((product, i) => <Link key={product.id} to={product.link} data-index={i} className="group flex flex-col items-center text-center gap-1 p-0 rounded-lg transition-transform duration-300 hover:scale-105">
          <div className="h-40 w-40 shrink-0 overflow-hidden rounded-md bg-gray-100">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center">
                <Package className="h-4 w-4 text-gray-300" />
              </div>}
          </div>
          <div className="flex flex-col opacity-80 mt-2">
            <h4 className="text-xs font-semibold text-gray-900 group-hover:text-primary transition-colors line-clamp-1">
              {product.title}
            </h4>
          </div>
        </Link>)}
    </div>;
}
function ProductsMegaMenuConsumer() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  useEffect(() => {
    if (terms.length > 0 && selectedCategoryId === null) {
      setSelectedCategoryId(terms[0].id);
    }
  }, [terms, selectedCategoryId]);
  if (loading) {
    return <div className="flex items-center justify-center p-12 w-full">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center p-8 text-center w-full">
        <CircleAlert className="h-8 w-8 text-destructive mb-2" />
        <p className="text-sm text-muted-foreground mb-4">Failed to load categories</p>
        <Button variant="outline" size="sm" onClick={refetch}>Retry</Button>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="p-12 text-center w-full">
        <p className="text-sm text-muted-foreground">No categories available</p>
      </div>;
  }
  const wp_query = new WP_Query({
    post_type: "product",
    per_page: 8,
    product_cat: selectedCategoryId ? selectedCategoryId.toString() : undefined
  });
  return <div className="flex min-h-[400px]">
      <div className="w-[28%] border-r border-border bg-gray-50/50 py-4 space-y-1">
        {terms.map((category, i) => <div key={category.id} data-index={i} onMouseEnter={() => setSelectedCategoryId(category.id)} className={cn("flex items-center gap-3 px-4 py-3 cursor-pointer transition-all duration-300 text-sm font-medium hover:scale-[1.02] hover:shadow-sm rounded-md mx-2", selectedCategoryId === category.id ? "bg-primary text-white shadow-md" : "text-gray-600 hover:bg-gray-100")}>
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-gray-200">
              {category.image_url ? <img src={category.image_url} alt={category.name} className="h-full w-full object-cover" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center">
                  <Package className="h-5 w-5 text-gray-400" />
                </div>}
            </div>
            <span className="flex-1 truncate">{category.name}</span>
            {selectedCategoryId === category.id && <ChevronRight className="h-4 w-4 shrink-0" />}
          </div>)}
      </div>

      <div className="w-[72%] p-2">
        {selectedCategoryId && <EcommerceProductsProvider key={selectedCategoryId} wp_query={wp_query}>
            <ProductsListConsumer />
          </EcommerceProductsProvider>}
      </div>
    </div>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const {
    terms
  } = useEcommerceTerms();
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
      {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-md" />)}
    </div>;
  }
  return <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-6 space-y-2">
        {menuItems.map((item, i) => {
        if (item.label === "Products") {
          return <div key={item.id} data-index={i} className="space-y-1">
                <Button variant="ghost" onClick={() => setIsProductsOpen(!isProductsOpen)} className="flex items-center justify-between w-full px-4 py-3 h-auto text-lg font-medium text-foreground hover:bg-accent rounded-md transition-colors">
                  {item.label}
                  <ChevronDown className={cn("h-5 w-5 transition-transform duration-300", isProductsOpen && "rotate-180")} />
                </Button>
                <div className={cn("overflow-hidden transition-all duration-300 ease-in-out", isProductsOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0")}>
                  <div className="pl-6 pr-4 py-2 space-y-1 border-l-2 border-primary/20 ml-4">
                    {terms.map((term, idx) => <Link key={term.id} to={term.link || "#"} data-index={idx} onClick={onClose} className="block py-2 text-base text-muted-foreground hover:text-primary transition-colors">
                        {term.name}
                      </Link>)}
                    <Link to="/ProductsPage" onClick={onClose} className="block py-2 text-base font-semibold text-primary hover:underline">
                      View All Products
                    </Link>
                  </div>
                </div>
              </div>;
        }
        return <Link key={item.id} to={item.href || "#"} data-index={i} onClick={onClose} className="block px-4 py-3 text-lg font-medium text-foreground hover:bg-accent rounded-md transition-colors">
              {item.label}
            </Link>;
      })}
      </div>
      <div className="p-4 border-t border-border">
        <Link to="/ContactPage" onClick={onClose}>
          <Button className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold py-6 text-lg rounded-lg shadow-lg">
            Contact Us
          </Button>
        </Link>
      </div>
    </div>;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const {
    terms
  } = useEcommerceTerms();
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMegaMenuOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
  const handleMouseEnter = () => {
    if (isMobile) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsMegaMenuOpen(true);
  };
  const handleMouseLeave = () => {
    if (isMobile) return;
    timeoutRef.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
    }, 150);
  };
  const handleToggle = (e: React.MouseEvent) => {
    if (isMobile) {
      e.preventDefault();
      setIsMegaMenuOpen(!isMegaMenuOpen);
    }
  };
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  const aboutItem = menuItems.find(item => item.label === "About");
  const otherItems = menuItems.filter(item => item.label !== "About" && item.label !== "Products");
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-x-8">
        {aboutItem && <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link to={aboutItem.href || "#"} className="text-sm font-default text-foreground relative py-1 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                {aboutItem.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>}

        <NavigationMenuItem onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="relative">
          <NavigationMenuLink asChild>
            <Link to="#" onClick={handleToggle} className="text-sm font-default text-foreground relative py-1 flex items-center gap-1 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
              Products
              <ChevronDown className={cn("h-3 w-3 transition-transform duration-300 hidden", isMegaMenuOpen && "rotate-180")} />
            </Link>
          </NavigationMenuLink>

          {isMegaMenuOpen && <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[90vw] max-w-5xl bg-background border border-border rounded-lg shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <ProductsMegaMenuConsumer />
              <div className="bg-muted/30 p-4 border-t border-border flex justify-center">
                <Link to="/ProductsPage" className="text-xs font-semibold uppercase tracking-wider text-primary hover:underline">
                  View All Products
                </Link>
              </div>
            </div>}
        </NavigationMenuItem>

        {otherItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-default text-foreground relative py-1 after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  const wp_term_query = new WP_Term_Query({
    taxonomy: "product_cat",
    per_page: 10
  });
  return <section data-section-id="252380" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-400 text-foreground backdrop-blur-xl border-b light", isScrolled ? "bg-white/15 shadow-lg border-white/30" : "bg-white/50 border-white/20")}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between py-4">
          
          <div className="flex-shrink-0">
            <Link to="/" className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
              <WvcLogo className="h-10 w-auto" />
            </Link>
          </div>

          <div className="flex items-center gap-x-4 md:gap-x-8">
            <EcommerceTermsProvider wp_term_query={wp_term_query}>
              <MenuProvider menu_id="46">
                <HeaderMenuConsumer />
                
                <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="md:hidden h-11 w-11" aria-label="Toggle Menu">
                      <Menu className="h-6 w-6" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[85vw] sm:w-[400px] p-0 flex flex-col">
                    <SheetHeader className="p-4 border-b border-border flex flex-row items-center justify-between">
                      <SheetTitle className="text-left">
                        <WvcLogo className="h-8 w-auto" />
                      </SheetTitle>
                    </SheetHeader>
                    <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
                  </SheetContent>
                </Sheet>
              </MenuProvider>
            </EcommerceTermsProvider>
            
            <Link to="/ContactPage">
              <Button variant="default" className="hidden md:inline-flex bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-semibold shadow-lg transition-transform hover:scale-105 active:scale-95">Contact Us</Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}